import { useEffect, useState } from 'react'
import { getEmployees } from '../../../crud/employees.crud'

// Helper functions/configurations for Engage.js
const APPLICATION_NAME_GWS = 'GWS'
const APPLICATION_NAME_M365 = 'Office365'
export const VISIBLE_LICENSES_QTY = 1
export const ENGAGE = 'engage'

/**
 * Retrieves the actions possible for a specific integration
 * @param {String} integrationType The application_name of the integration
 */
export const getActionsToDisplayForIntegration = () => {
  // TODO
  // Once we have more time, Engage.js needs to be refactored to handle formatting the display/actions dynamically based on the integration type rather than having them hardcoded
  // Leaving this as a todo for next sprint
}

/**
 * Retrieves an array of columns in the order that they should be rendered in the main engage view for each integration
 * @param {String} integrationType The selected integration application name
 */
// eslint-disable-next-line consistent-return
export const getIntegrationColumns = (integrationType) => {
  switch (integrationType) {
    case APPLICATION_NAME_GWS:
      return ['employees', 'departments', 'licenses', 'groups', 'mfaStatus', 'actions']
    case APPLICATION_NAME_M365:
      return ['employees', 'departments', 'licenses', 'mfaStatus', 'actions']
    default:
      console.error('Error - Invalid application name provided to getIntegrationColumns')
      break
  }
}

/**
 * Retrieves the column labels to display in the main engage table
 * @param {String} integrationType The application_name of the selected integration
 * @param {Object} intl The language formatter object
 */
export const getColumnLabels = (integrationType, intl) => {
  const columnLabels = {}

  // Columns used by all current integrationTypes
  columnLabels.employeeLabel = intl.formatMessage({
    id: 'ADMINISTRATION.EMPLOYEE',
  }) // Employee
  columnLabels.emailLabel = intl.formatMessage({ id: 'ADMINISTRATION.EMAIL' }) // Employee
  columnLabels.departmentLabel = intl.formatMessage({
    id: 'ADMINISTRATION.DEPARTMENT',
  }) // Department
  columnLabels.licenseTypeLabel = intl.formatMessage({
    id: 'ENGAGE.LICENSE_TYPE',
  }) // License Type
  columnLabels.mfaStatusLabel = intl.formatMessage({
    id: 'ADMINISTRATION.MFA_STATUS',
  }) // MFA Status
  columnLabels.actionsLabel = intl.formatMessage({ id: 'DASHBOARD.ACTIONS' }) // Actions
  columnLabels.groupsLabel = intl.formatMessage({ id: 'ENGAGE.GROUPS' }) // Groups

  return columnLabels
}

// eslint-disable-next-line consistent-return
const getMultiSelectColumnConfigs = (integrationType) => {
  switch (integrationType) {
    case APPLICATION_NAME_GWS:
      return {
        department: 'departments',
        licenseType: 'licenseTypes',
        // groups: "groups", // May change depending on payload from probe
        mfaEnabled: 'mfaTypes',
      }
    case APPLICATION_NAME_M365:
      return {
        department: 'departments',
        licenseType: 'licenseTypes',
        mfaEnabled: 'mfaTypes',
      }
    default:
      console.error('[multiSelectColumnConfig Error] - Invalid integration application name provided')
      break
  }
}

/**
 * Creates a map of dependencies between multi-select columns to allow for cascaded filtering
 *
 * Example filterMap = {
 *    departments: [
 *       {
 *          "Engineering": {
 *             licenseTypes: ["M365 Free", "M365 Vault"],
 *             mfaTypes: [true, false]
 *          }
 *       },
 *       ...
 *    ],
 *    licenseTypes: [
 *       {
 *          "M365 Free": {
 *             departments: ["Engineering"],
 *             mfaTypes: [true, false]
 *          },
 *          {
 *            ...
 *          }
 *       }
 *    ],
 *    ...
 * }
 * @param {String} integrationType The application_name of the selected integration
 * @param {Array} employees The employees to build the filter map for
 */
export const createFilterMap = (integrationType, employees) => {
  const filterMap = {}
  const multiSelectColumnConfig = getMultiSelectColumnConfigs(integrationType)
  const multiSelectColumns = Object.values(multiSelectColumnConfig)

  // Setup the structure of the filterMap
  // eslint-disable-next-line no-restricted-syntax
  for (const multiSelectColumn of multiSelectColumns) {
    filterMap[multiSelectColumn] = []
  }

  // Construct the filterMap out of employee data based on the multi-select column relationships
  for (let i = 0; i < employees.length; i += 1) {
    // eslint-disable-next-line no-restricted-syntax
    for (const [employeeField, column] of Object.entries(multiSelectColumnConfig)) {
      const { [employeeField]: discardedEmployeeField, ...columnObjectsToAdd } = multiSelectColumnConfig // These are the key values to add to the column object sets (Example: if the column is departments, the columnObjectsToAdd may be { licenseType: licenseTypes, ... })

      // The key of the column object (Example: if the column is departments, this key may be "Engineering")
      let columnObjectKeys = employees[i][employeeField]
      if (!Array.isArray(columnObjectKeys)) {
        columnObjectKeys = [columnObjectKeys] // Ensure keys are in an array
      }

      // Special column-type cases
      const columnObjectKeysTemp = []
      if (column === 'licenseTypes') {
        // licenseTypes is going to be an array of license objects, use the displayName field as the column object key
        // eslint-disable-next-line no-restricted-syntax
        for (const license of columnObjectKeys) {
          columnObjectKeysTemp.push(license.displayName)
        }
        columnObjectKeys = columnObjectKeysTemp
      }

      // eslint-disable-next-line no-restricted-syntax
      for (const columnObjectKey of columnObjectKeys) {
        const indexOfColumnObject = filterMap[column].findIndex(
          (columnObject) => Object.keys(columnObject)[0] === columnObjectKey.toString(),
        )

        if (indexOfColumnObject !== -1) {
          // Column object already has an entry, so just update the data in the sets
          // eslint-disable-next-line no-restricted-syntax
          for (const [employeeFieldToAdd, columnToAdd] of Object.entries(columnObjectsToAdd)) {
            filterMap[column][indexOfColumnObject][columnObjectKey][columnToAdd].add(employees[i][employeeFieldToAdd])
          }
        } else {
          // Column object does not exist yet for this column, so create it
          const newlyAddedColumnObjectIndex = filterMap[column].push({ [columnObjectKey]: {} }) - 1

          // Add the data to the newly create column object
          // eslint-disable-next-line no-restricted-syntax
          for (const [employeeFieldToAdd, columnToAdd] of Object.entries(columnObjectsToAdd)) {
            if (employeeFieldToAdd === 'licenseType') {
              filterMap[column][newlyAddedColumnObjectIndex][columnObjectKey][columnToAdd] = new Set()

              for (let j = 0; j < employees[i][employeeFieldToAdd].length; j += 1) {
                filterMap[column][newlyAddedColumnObjectIndex][columnObjectKey][columnToAdd].add(
                  employees[i][employeeFieldToAdd][j].displayName,
                )
              }
            } else {
              filterMap[column][newlyAddedColumnObjectIndex][columnObjectKey][columnToAdd] = new Set().add(
                employees[i][employeeFieldToAdd],
              )
            }
          }
        }
      }
    }
  }

  return filterMap
}

/**
 * Makes sure the licenses being filtered appear before any other license
 * @param {array} rowLicenses All company's licenses
 * @param {array} filterLicenses The active licenses on the filter
 * @returns The licenses labels, with the filter ones first
 */
export const getOrderedLicenses = (rowLicenses = [], filterLicenses = []) => {
  const unfilteredLicenses = rowLicenses
    .filter((license) => !filterLicenses.find((item) => item.value === license.id))
    .map((license) => license.displayName)

  const filteredLicenses = filterLicenses
    .filter((license) => rowLicenses.find((item) => item.id === license.value))
    .map((license) => license.label)

  return [...filteredLicenses, ...unfilteredLicenses]
}

/**
 * Restructure to match format of allLicensedEmployees.
 * Preserve integration fields from the Engage employee list (especially GWS licenseType).
 */
export const adaptEmployeesForEngage = (employees = []) =>
  employees.map((e) => ({
    integrationEmployeeId: e.integration_employee_id ?? e.integrationEmployeeId,
    employeeId: e.employeeId ?? e.id,
    email: e.email,
    firstName: e.firstName ?? e.first_name,
    lastName: e.lastName ?? e.last_name,
    displayName: e.displayName ?? e.display_name,
    department: e.department,
    employeeIdentifier: e.employeeIdentifier ?? e.employee_identifier,
    enabled: e.enabled,
    jobTitle: e.jobTitle ?? e.job_title,
    licenseType: Array.isArray(e.licenseType) ? e.licenseType : [],
    mfaEnabled: e.mfaEnabled ?? e.mfa_enabled,
    isAdmin: e.isAdmin,
    isAuthorizedForEngage: e.isAuthorizedForEngage,
    licenses: e.licenses,
    groups: e.groups,
  }))

export const useEngageEmployees = ({ selectedCustomerId }) => {
  const [engageEmployees, setEngageEmployees] = useState([])

  useEffect(() => {
    const getCustomerEmployees = async () => {
      const select = ['id', 'email', 'first_name', 'last_name', 'display_name', 'department', 'job_title', 'licenses'] // select only the necessary employee properties
      const employees = await getEmployees(selectedCustomerId, { select })
      setEngageEmployees(adaptEmployeesForEngage(employees?.data))
    }

    if (selectedCustomerId) {
      getCustomerEmployees()
    }
  }, [selectedCustomerId])

  return engageEmployees
}
